import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { getOrderPermissions } from './api';
import { OrderPermissions } from './types';

interface Props {
  children: (permissions: OrderPermissions) => ReactNode;
  requireManage?: boolean;
  // My assignments: the user's own assignments instead of management access.
  own?: boolean;
}

const isAllowed = (
  { canManage, canViewAll, canViewOwn }: OrderPermissions,
  requireManage: boolean,
  own: boolean,
) => {
  if (own) {
    return canViewOwn;
  }
  return requireManage ? canManage : canManage || canViewAll;
};

const OrderAccess = ({
  children,
  requireManage = false,
  own = false,
}: Props) => {
  const intl = useIntl();
  const [permissions, setPermissions] = useState<OrderPermissions | null>(null);

  useEffect(() => {
    let mounted = true;
    getOrderPermissions().then((result) => mounted && setPermissions(result));
    return () => {
      mounted = false;
    };
  }, []);

  if (!permissions) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress
          aria-label={intl.formatMessage({ id: 'ORDERS.LOADING' })}
        />
      </Box>
    );
  }

  if (permissions.unavailable) {
    return (
      <Alert severity="error">
        {intl.formatMessage({ id: 'ORDERS.ERROR' })}
      </Alert>
    );
  }

  if (!isAllowed(permissions, requireManage, own)) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5">
          {intl.formatMessage({
            id: own ? 'MY_ORDERS.NO_ACCESS' : 'ORDERS.NO_ACCESS',
          })}
        </Typography>
      </Box>
    );
  }

  return <>{children(permissions)}</>;
};

export default OrderAccess;
